import { useMemo, useState } from 'react';
import type { Reservation } from '@vinz/shared';
import { RESERVABLE } from '@vinz/shared';
import { useAuth } from '../auth.js';
import { formatDuration, formatLocal, viewerTimeZone } from '../lib/format.js';
import { useHistoryQuery } from '../use-api.js';

// Mirrors packages/api/src/handlers/history.ts's DemandRow. Not published from @vinz/shared, so it
// is redeclared here rather than imported across the Lambda/portal boundary. `extensions` is counted
// in the roll-up itself (Store.recordHold ADDs it alongside holds/heldSeconds/holders) and summed
// there by the handler, the same way holds and heldSeconds are - never approximated client-side from
// the capped holds sample, which cannot be reconstructed once individual hold rows TTL out.
export interface DemandRow {
  repo: string;
  holds: number;
  people: number;
  occupancyPct: number;
  meanHoldSeconds: number;
  lanesCandidate: boolean;
  extensions: number;
}

export interface HistoryResponse {
  demand: DemandRow[];
  holds: Record<string, unknown>[];
}

// The lanes decision (00-reserved-region-model.md §10) is Dave's call, made off the API's own
// `lanesCandidate` flag. The 80% threshold that sets that flag lives in exactly one place -
// LANES_OCCUPANCY_THRESHOLD in packages/api/src/handlers/history.ts - and is never re-derived here.

const PERIODS = [
  { value: '90d', label: 'Last 90 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
] as const;

type Period = (typeof PERIODS)[number]['value'];

const DAY_MS = 24 * 3600 * 1000;

// "All time" has to name a start. Omitting `from` is not the same thing: the API reads a missing
// `from` as its own 90-day default, which made "All time" and "Last 90 days" return byte-identical
// results. The epoch is earlier than any roll-up can be, and the API clamps the window it divides by
// to the earliest month it actually recorded, so the occupancy column stays meaningful.
const ALL_TIME_FROM = new Date(0).toISOString();

function periodFrom(period: Period, nowMs: number): string {
  switch (period) {
    case '90d':
      return new Date(nowMs - 90 * DAY_MS).toISOString();
    case '30d':
      return new Date(nowMs - 30 * DAY_MS).toISOString();
    case '12m':
      return new Date(nowMs - 365 * DAY_MS).toISOString();
    case 'all':
    default:
      return ALL_TIME_FROM;
  }
}

interface HoldView {
  id: string;
  repo: string;
  who: string;
  reason: string;
  startedIso?: string;
  durationSeconds?: number;
  extensions: number;
}

/**
 * A hold row from the API is `Record<string, unknown>` - it is a deployment-history item that
 * optionally carries the reservation payload that was live at the time. Field access is defensive
 * throughout: `actor`/`deployedAt` are the deploy-observation fallback, `reservation.*` is the
 * richer, hold-specific detail when present.
 */
function toHoldView(row: Record<string, unknown>, index: number): HoldView {
  const repo = typeof row.repo === 'string' ? row.repo : '';
  const workspace = typeof row.workspace === 'string' ? row.workspace : '';
  const reservation = row.reservation as Partial<Reservation> | undefined;
  const actor = typeof row.actor === 'string' ? row.actor : undefined;
  const deployedAt = typeof row.deployedAt === 'string' ? row.deployedAt : undefined;

  const who = reservation?.holder ?? actor ?? '—';
  const reason = reservation?.reason ?? '—';
  const startedIso = reservation?.granted_at ?? deployedAt;
  const durationSeconds =
    reservation?.granted_at && reservation?.expires_at
      ? Math.round((Date.parse(reservation.expires_at) - Date.parse(reservation.granted_at)) / 1000)
      : undefined;

  return {
    id: `${repo}#${workspace}#${startedIso ?? index}`,
    repo,
    who,
    reason,
    startedIso,
    durationSeconds,
    extensions: reservation?.extensions ?? 0,
  };
}

function distinctPeople(holds: Record<string, unknown>[]): string[] {
  const set = new Set<string>();
  holds.forEach((row, i) => {
    const { who } = toHoldView(row, i);
    if (who !== '—') set.add(who);
  });
  return [...set].sort();
}

function overallMeanHoldSeconds(demand: DemandRow[]): number {
  let totalHeldSeconds = 0;
  let totalHolds = 0;
  for (const d of demand) {
    totalHeldSeconds += d.meanHoldSeconds * d.holds;
    totalHolds += d.holds;
  }
  return totalHolds > 0 ? Math.round(totalHeldSeconds / totalHolds) : 0;
}

/**
 * Bar colour carries the same signal as the lanes flag, so the flag is never re-derived here: red is
 * the API's own `lanesCandidate`. Amber at half that contention and above is presentation only - it
 * draws the eye to the services approaching it, and decides nothing.
 */
function occupancyTone(row: DemandRow): string {
  if (row.lanesCandidate) return 'var(--loop-red-600)';
  if (row.occupancyPct >= 40) return 'var(--loop-yellow-800)';
  return 'var(--loop-green-600)';
}

function OccupancyCell({ row }: { row: DemandRow }) {
  const clamped = Math.min(100, Math.max(0, row.occupancyPct));
  return (
    <>
      <span className="occ" role="progressbar" aria-valuenow={Math.round(clamped)} aria-valuemin={0} aria-valuemax={100}>
        <i style={{ width: `${clamped}%`, background: occupancyTone(row) }} />
      </span>
      <span className="strong-medium">{row.occupancyPct}%</span>
    </>
  );
}

export interface DemandTableProps {
  demand: DemandRow[];
}

/** The main table: which services are contended, led by occupancy. Plain props - no network, no auth. */
export function DemandTable({ demand }: DemandTableProps) {
  return (
    <table className="grid grid-compact">
      <thead>
        <tr>
          <th scope="col" style={{ width: '26%' }}>Service</th>
          <th scope="col">Holds</th>
          <th scope="col">People</th>
          <th scope="col" style={{ width: '26%' }}>Occupancy</th>
          <th scope="col">Mean hold</th>
          <th scope="col">Extensions</th>
        </tr>
      </thead>
      <tbody>
        {demand.map((row) => (
          <tr key={row.repo}>
            <td className="strong-medium">
              {row.repo}
              {row.lanesCandidate && <span className="lanes-badge">lanes candidate</span>}
            </td>
            <td>{row.holds}</td>
            <td>{row.people}</td>
            <td><OccupancyCell row={row} /></td>
            <td>{row.holds > 0 ? formatDuration(row.meanHoldSeconds) : '—'}</td>
            <td>{row.extensions}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HoldsTable({ holds, person }: { holds: Record<string, unknown>[]; person?: string }) {
  const rows = holds.map((row, i) => toHoldView(row, i));
  return (
    <table className="grid grid-compact">
      <thead>
        <tr>
          <th scope="col" style={{ width: '16%' }}>Service</th>
          <th scope="col" style={{ width: '16%' }}>Who</th>
          <th scope="col">Reason</th>
          <th scope="col" style={{ width: '14%' }}>Started</th>
          <th scope="col" style={{ width: '10%' }}>Duration</th>
          <th scope="col" style={{ width: '8%' }}>Ext.</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={6} className="muted">{person ? `No holds by ${person} in this period.` : 'No holds in this period.'}</td></tr>
        )}
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="strong-medium">{row.repo}</td>
            <td>{row.who}</td>
            <td className="muted">{row.reason}</td>
            <td>{row.startedIso ? formatLocal(row.startedIso) : '—'}</td>
            <td>{row.durationSeconds !== undefined ? formatDuration(row.durationSeconds) : '—'}</td>
            <td>{row.extensions}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SummaryTiles({ demand, holds }: { demand: DemandRow[]; holds: Record<string, unknown>[] }) {
  const holdsGranted = demand.reduce((sum, d) => sum + d.holds, 0);
  const peopleCount = distinctPeople(holds).length;
  const everHeld = demand.filter((d) => d.holds > 0).length;
  const meanHold = overallMeanHoldSeconds(demand);
  return (
    <div className="tile-row">
      <div className="tile"><b>{holdsGranted}</b><span>holds granted</span></div>
      <div className="tile"><b>{peopleCount}</b><span>distinct people</span></div>
      <div className="tile">
        <b>{everHeld} <span className="tile-of">of {RESERVABLE.length}</span></b>
        <span>services ever held</span>
      </div>
      <div className="tile"><b>{holdsGranted > 0 ? formatDuration(meanHold) : '—'}</b><span>mean hold</span></div>
    </div>
  );
}

function LanesCallout({ demand }: { demand: DemandRow[] }) {
  // demand is already sorted by occupancy descending (the API's own ordering, never re-sorted
  // here), so the first lanesCandidate row found is also the most contended one.
  const candidate = demand.find((d) => d.lanesCandidate);
  if (!candidate) return null;
  return (
    <div className="callout-brand" role="status">
      <span aria-hidden="true" className="callout-mark">ⓘ</span>
      <div>
        <strong>
          {candidate.repo} is held {candidate.occupancyPct}% of the time, by {candidate.people} different{' '}
          {candidate.people === 1 ? 'person' : 'people'} — a lanes candidate.
        </strong>{' '}
        That is the contention this table exists to surface: past the lanes threshold a service is flagged as worth
        building <em>lanes</em> for, per the design’s §10.
      </div>
    </div>
  );
}

interface FilterBarProps {
  service: string;
  period: Period;
  onServiceChange: (value: string) => void;
  onPeriodChange: (value: Period) => void;
}

function FilterBar({ service, period, onServiceChange, onPeriodChange }: FilterBarProps) {
  return (
    <div className="filter-bar">
      <div className="filter-field">
        <label htmlFor="history-filter-service">Service</label>
        <select
          id="history-filter-service"
          className="user-filter"
          value={service}
          onChange={(e) => onServiceChange(e.target.value)}
        >
          <option value="">All services</option>
          {RESERVABLE.map((s) => (
            <option key={s.repo} value={s.repo}>{s.repo}</option>
          ))}
        </select>
      </div>
      <div className="filter-field">
        <label htmlFor="history-filter-period">Period</label>
        <select
          id="history-filter-period"
          className="user-filter"
          value={period}
          onChange={(e) => onPeriodChange(e.target.value as Period)}
        >
          {PERIODS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/**
 * Scoped to the holds table and rendered beside its heading, not in the filter bar above: the demand
 * table and tiles come from roll-ups, which count holders as a set per month and cannot be split by
 * person. A Person control beside Service and Period read as filtering everything while changing only
 * this. Styled exactly like the bar's controls so it still reads as part of the same page.
 */
function PersonFilter({ person, people, onPersonChange }: {
  person: string; people: string[]; onPersonChange: (v: string) => void;
}) {
  return (
    <div className="filter-field">
      <label htmlFor="history-filter-person">Person</label>
      <select
        id="history-filter-person"
        className="user-filter"
        value={person}
        onChange={(e) => onPersonChange(e.target.value)}
      >
        <option value="">Anyone</option>
        {people.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
    </div>
  );
}

export function History() {
  const { idToken } = useAuth();
  const [service, setService] = useState('');
  const [period, setPeriod] = useState<Period>('90d');
  const [person, setPerson] = useState('');

  // Memoised on the period: `from` is in the query key, so recomputing it from Date.now() on every
  // render gave every render a new key and started another request - the page could refetch forever.
  const from = useMemo(() => periodFrom(period, Date.now()), [period]);
  const { data, isPending, isError, error } = useHistoryQuery(idToken, { service: service || undefined, from });

  const holds = data?.holds ?? [];
  const people = useMemo(() => distinctPeople(holds), [holds]);
  const filteredHolds = person ? holds.filter((row, i) => toHoldView(row, i).who === person) : holds;
  const demand = data?.demand ?? [];
  const periodLabel = PERIODS.find((p) => p.value === period)?.label.toLowerCase() ?? '';

  if (isPending) return <div className="card">Loading history…</div>;
  if (isError) {
    return <div className="card">Couldn’t load history: {error instanceof Error ? error.message : String(error)}</div>;
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h2>History</h2>
          <p>How much staging is being reserved, by how many people, and which services are contended enough to need lanes.</p>
        </div>
        <div className="page-head-meta">Times in {viewerTimeZone()}</div>
      </div>
      <FilterBar
        service={service}
        period={period}
        onServiceChange={setService}
        onPeriodChange={setPeriod}
      />
      <SummaryTiles demand={demand} holds={holds} />
      <LanesCallout demand={demand} />
      <section className="history-section">
        <h3>Demand by service — {periodLabel}</h3>
        <div className="table-card"><DemandTable demand={demand} /></div>
      </section>
      <section className="history-section">
        <div className="section-head">
          <h3>Individual holds</h3>
          <PersonFilter person={person} people={people} onPersonChange={setPerson} />
        </div>
        <div className="table-card"><HoldsTable holds={filteredHolds} person={person || undefined} /></div>
      </section>
      <p className="page-note">
        Vinz grants no holds until R5, so these tables fill as it starts to. Occupancy and distinct people are counted
        {' '}<strong>at write time</strong> into the monthly roll-up; individual holds expire after 12 months and cannot be
        re-derived afterwards.
      </p>
    </div>
  );
}
